import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Chart } from 'react-google-charts';
import polyglotUIComponents from '../../../i18n';
import rgb2hex, {
  convertTimestampMilliseconds,
  datesAreOnSameDay,
} from '../../../util/Util';
import {
  colorsScale,
  colorsModeUI,
  COLOR_SCALE_PRIMARY_COLORS,
  COLOR_SCALE_CHARTS_DIGITAL_CHANNELS_COLORS,
} from '../../../modeUI';
import { DATE_FORMAT, DATETIME_FORMAT, TIME_FORMAT } from '../../../constants';
import SortUtil from '../../../util/SortUtil';
import { getDateFormatGoogleCharts, getDateFormat } from '../../constants';
import './index.css';
import { getLabelsFromData, TimeLineLabels } from './components/TimeLineLabels';
// create a style option to set opacity to 0
let blankStyle = 'stroke-width: 0;fill-opacity: 0;';

/* SIZES CHART:
**************************************************************************
* LEGENDA *  WIDHT ONLY CHARTS                                           *
* WIDTH   *                                                              *
*         *                                                              *
**************************************************************************

/**
* TimeLines
* Display charts type TYPE_CHART_LINECHART
* parameters (props)
* @param object data = = { [eventId]: {name, id, data: [start, end]} }
* @param string mode
* @param string language
* @param number width: chart's width including the size of the widthVAxis
* @param number widthVAxis: legenda's height
* @param number minDatetim: timestamp format: min value view port haxis
* @param number maxDatetim: timestamp format: max value view port haxis
*/
export default class TimeLines extends Component {
  /**
   * Test width
   * @param string text
   * @return number size
   */
  static textWidth(text) {
    const sizeName = document.createElement('div');
    sizeName.style.position = 'absolute';
    sizeName.style.left = '-999em';
    sizeName.style.whiteSpace = 'nowrap';
    sizeName.style.font = '12px Arial';
    sizeName.innerHTML = text;
    document.body.appendChild(sizeName);
    const size = sizeName.clientWidth;
    document.body.removeChild(sizeName);
    return size;
  }

  /**
   * Reduce text to width
   * @param string text
   * @param number width
   * @return string newText
   */
  static reduceTextToWidth(text, width) {
    let exit = false;
    let reduceCharacteres = 1;
    let newText = text.substr(0, text.length - reduceCharacteres);
    while (!exit || text.length === reduceCharacteres) {
      const size = TimeLines.textWidth(newText);
      if (size <= width) {
        exit = true;
      } else {
        reduceCharacteres += 1;
        newText = text.substr(0, text.length - reduceCharacteres);
      }
    }
    return newText;
  }
  state = { ready: false };
  constructor(props) {
    super(props);
    this.toolTip = this.toolTip.bind(this);
    this.getDuration = this.getDuration.bind(this);
    this.determineDateFormatAxis = this.determineDateFormatAxis.bind(this);
    this.createSeries = this.createSeries.bind(this);
    this.polyglot = polyglotUIComponents(props.language);
  }

  /**
   * Get duration
   * Note: start and end could be in seconds or miliseconds, because of that we need
   * to check the value of this parameters
   * @param int start = timestamp in seconds or miliseconds
   * @param int end = timestamp in seconds or miliseconds
   */
  getDuration(start, end) {
    const minTS = moment(convertTimestampMilliseconds(start));
    const maxTS = moment(convertTimestampMilliseconds(end));
    const diff = maxTS.diff(minTS);
    const duration = moment.duration(diff);
    let durationString = '';
    if (duration.get('years') > 0) {
      durationString = `${duration.get('years')} ${
        duration.get('years') === 1
          ? this.polyglot.t('general.year')
          : this.polyglot.t('general.years')
      } `;
    }
    if (duration.get('months') > 0) {
      durationString += `${duration.get('months')} ${
        duration.get('months') === 1
          ? this.polyglot.t('general.month')
          : this.polyglot.t('general.months')
      } `;
    }
    if (duration.get('days') > 0) {
      durationString += `${duration.get('days')} ${
        duration.get('days') === 1
          ? this.polyglot.t('general.day')
          : this.polyglot.t('general.days')
      } `;
    }
    if (duration.get('hours') > 0) {
      durationString += `${duration.get('hours')} ${
        duration.get('hours') === 1
          ? this.polyglot.t('general.hour')
          : this.polyglot.t('general.hours')
      } `;
    }
    if (duration.get('minutes') > 0) {
      durationString += `${duration.get('minutes')} ${
        duration.get('minutes') === 1
          ? this.polyglot.t('general.minute')
          : this.polyglot.t('general.minutes')
      } `;
    }
    if (duration.get('seconds') > 0) {
      durationString += `${duration.get('seconds')} ${
        duration.get('seconds') === 1
          ? this.polyglot.t('general.second')
          : this.polyglot.t('general.seconds')
      } `;
    }

    return durationString.trim();
  }

  /**
   * Tooltip
   * @param string event
   * @param object {start,end,fakeEnd,fakeStart}
   */
  toolTip(event, data) {
    const { start, end, fakeEnd, fakeStart } = data;
    const duration = this.getDuration(start, end);
    let durationText = `<br /><b>${this.polyglot.t(
      'general.duration'
    )}:</b> ${duration}`;
    let rangeTimeText = '';
    let startText = '';
    let endText = '';
    if (datesAreOnSameDay(start, end) && !fakeStart && !fakeEnd) {
      rangeTimeText = `<b>${moment.unix(start).format(DATE_FORMAT)}</b><br />`;
      startText = moment.unix(start).format(TIME_FORMAT);
      endText = moment.unix(end).format(TIME_FORMAT);
    } else {
      startText = moment.unix(start).format(DATETIME_FORMAT);
      endText = moment.unix(end).format(DATETIME_FORMAT);
    }
    if (fakeStart || fakeEnd) {
      durationText = '';
    }
    if (!fakeStart) {
      rangeTimeText += `${this.polyglot.t('general.from')} ${startText}<br />`;
    }
    if (!fakeEnd) {
      rangeTimeText += `${this.polyglot.t('general.to')} ${endText}<br />`;
    }

    return `<div class="tooltip-timeline-chart">
            ${event} <br /><hr />
            ${rangeTimeText}
            ${durationText}
            </div>
    `;
  }

  /**
   * Create series
   * @return array dataTimeLineChart = [[array with the columns definition],
   * ... data chart[{event, '', 'tooltip', 'start', 'end' }]]
   */
  createSeries() {
    const { data, labels } = this.props;
    const { widthVAxis, type } = labels;
    const withLabelsHTML = type === 'HTML';

    const paddingText = 20;

    /* the number of characters displayed of the event name depends on the width
    defined into the variable widthVAxis */
    if (widthVAxis && !withLabelsHTML) {
      Object.values(data).forEach((value) => {
        let { name } = value;
        const nameOriginal = name;
        const textWidth = TimeLines.textWidth(name);
        if (textWidth > widthVAxis - paddingText) {
          name = TimeLines.reduceTextToWidth(name, widthVAxis - paddingText);
        } else {
          const add = Math.ceil((widthVAxis - textWidth - paddingText) / 4);
          name = `${'.'.repeat(add)}${name}`;
        }
        Object.assign(value, { name, nameOriginal });
      });
    }
    const orderedListData = SortUtil.multisort(
      Object.values(data),
      ['name'],
      ['ASC']
    );
    const dataTimeLineChart = [
      [
        { type: 'string', id: 'Event' },
        { type: 'string', id: 'dump label' },
        { type: 'string', role: 'tooltip' },
        { id: 'style', type: 'string', role: 'style' },
        { type: 'date', id: 'Start' },
        { type: 'date', id: 'End' },
      ],
    ];
    orderedListData.forEach((value) => {
      value.data.forEach((d) => {
        if (d.start) {
          const newEntry = [];
          newEntry.push(value.name);
          newEntry.push('');
          newEntry.push(
            this.toolTip(
              value.nameOriginal ? value.nameOriginal : value.name,
              d
            )
          );
          newEntry.push('');
          newEntry.push(moment.unix(d.start).toDate());
          newEntry.push(
            d.end ? moment.unix(d.end).toDate() : moment.unix(d.start).toDate()
          );
          dataTimeLineChart.push(newEntry);
        }
      });
    });

    // Add empty rows
    const firstDate = dataTimeLineChart[1] && dataTimeLineChart[1][4];
    if (firstDate) {
      orderedListData
        .filter((value) => value.data.length === 0)
        .forEach((value) => {
          // create empty boxes to show the row
          dataTimeLineChart.push([
            value.name,
            '',
            '',
            blankStyle,
            firstDate,
            dataTimeLineChart[1][4],
          ]);
        });
    }

    return dataTimeLineChart;
  }

  /**
   * Determine date format axis
   * This function searches the min and max dates used to determinate the format
   * @return string DATEFORMAT
   */
  determineDateFormatAxis() {
    const { data, minDatetime, maxDatetime, labels } = this.props;

    // remove axis replacing with empty space
    if (!labels.showHAxis) {
      return ' ';
    }

    let dataTemp = [];
    Object.values(data).forEach((value) => {
      dataTemp = dataTemp.concat(value.data);
    });
    dataTemp = dataTemp.filter(
      (d) =>
        d.start !== null &&
        d.start !== undefined &&
        d.end !== null &&
        d.end !== undefined
    );
    SortUtil.multisort(dataTemp, ['start'], ['ASC']);
    const start = !minDatetime ? dataTemp[0].start : minDatetime;
    SortUtil.multisort(dataTemp, ['end'], ['DESC']);
    const end = !maxDatetime ? dataTemp[0].start : maxDatetime;
    return getDateFormatGoogleCharts(getDateFormat(start, end));
  }

  render() {
    const {
      data,
      mode,
      width,
      minDatetime,
      maxDatetime,
      labels,
      ...otherProps
    } = this.props;
    const { widthVAxis } = labels;
    const withLabelsHTML = labels.type === 'HTML';
    const dataTimeLineChart = this.createSeries();
    if (dataTimeLineChart.length === 1) {
      return <div />;
    }
    const dateFormatAxis = this.determineDateFormatAxis(dataTimeLineChart);
    /* options charts */
    const options = {
      allowHtml: true,
      hAxis: {
        // remove axis replacing with empty space
        format: dateFormatAxis,
        titleTextStyle: {
          fontSize: 12,
        },
      },
      vAxis: {
        titleTextStyle: {
          fontSize: 12,
        },
      },
      timeline: {
        showRowLabels: withLabelsHTML ? false : undefined,
        showBarLabels: false,
        rowLabelStyle: { fontSize: 12 },
      },
    };
    /* Add colors definition */
    let colorHAxisText = '#ffffff';
    const modeColors = colorsModeUI(mode);
    colorHAxisText = modeColors.INFORMATION_COLOR;
    const colorPalette = colorsScale(
      mode,
      Object.keys(data).length,
      Object.keys(data).length < 5
        ? COLOR_SCALE_PRIMARY_COLORS
        : COLOR_SCALE_CHARTS_DIGITAL_CHANNELS_COLORS
    );
    Object.assign(
      options,
      { colors: colorPalette },
      {
        backgroundColor: {
          fill: rgb2hex(modeColors.CONTROL_UNIT_DETAILS_BACKGROUND_COLOR),
          fillOpacity: 0.1,
        },
      }
    );
    /* Add min value and max value hAxis */
    if (minDatetime || maxDatetime) {
      if (minDatetime) {
        /* minDatetime = format timestamp */
        const minValueDatetime = new Date(
          convertTimestampMilliseconds(minDatetime)
        );
        Object.assign(options.hAxis, { minValue: minValueDatetime });
      }
      if (maxDatetime) {
        /* maxDatetime = format timestamp */
        const maxValueDatetime = new Date(
          convertTimestampMilliseconds(maxDatetime)
        );
        Object.assign(options.hAxis, { maxValue: maxValueDatetime });
      }
    }
    const labelsList = getLabelsFromData(dataTimeLineChart);
    // 40.95 size each row, 60 labels time
    const sizeHAxis = 60;
    const height = labelsList.length * 40.95 + sizeHAxis;
    // http://jira.st.local/jira/browse/WEB-555
    const minHeight = 215;
    const widthChart = !width
      ? window.innerWidth * 0.01 * 95 - withLabelsHTML
      : width;
    const chartEvents = [
      {
        eventName: 'ready',
        callback: ({ chartWrapper }) => {
          const chart = chartWrapper.getChart();
          this.setState((state) => {
            if (!state.ready) {
              return { ready: true };
            }
            return null;
          });
          const eventReady = google.visualization.events.addListener(
            chart,
            'ready',
            () => {
              const chartHtml =
                document.getElementsByClassName('chart-timeline');
              if (chartHtml.length > 0) {
                const labels = chartHtml[0].getElementsByTagName('text');
                Array.prototype.forEach.call(labels, (label) => {
                  if (
                    label.getAttribute('text-anchor') === 'middle' ||
                    label.getAttribute('text-anchor') === 'end'
                  ) {
                    label.setAttribute('fill', colorHAxisText);
                  }
                });
              }
              google.visualization.events.removeListener(eventReady);
            }
          );
        },
      },
    ];
    return (
      <div
        id="timeLineChart"
        className="TimeLineChart__Container"
        style={{ width: widthChart + widthVAxis }}
      >
        {withLabelsHTML && this.state.ready && (
          <TimeLineLabels labels={labelsList} width={widthVAxis} mode={mode} />
        )}
        <Chart
          className="chart-timeline"
          height={height}
          chartType="Timeline"
          loader={<div>Loading Chart</div>}
          data={dataTimeLineChart}
          options={options}
          chartEvents={chartEvents}
          style={{ height: height, width: widthChart, minHeight }}
          {...otherProps}
        />
      </div>
    );
  }
}

TimeLines.propTypes = {
  mode: PropTypes.string,
  data: PropTypes.objectOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      data: PropTypes.arrayOf(
        PropTypes.shape({
          start: PropTypes.number.isRequired,
          end: PropTypes.number.isRequired,
        })
      ).isRequired,
    }).isRequired
  ).isRequired,
  language: PropTypes.string,
  width: PropTypes.number,
  minDatetime: PropTypes.number /* timestamp format */,
  maxDatetime: PropTypes.number /* timestamp format */,
  labels: PropTypes.shape({
    type: PropTypes.string.isRequired,
    widthVAxis: PropTypes.number,
    showHAxis: PropTypes.bool,
  }),
};

TimeLines.defaultProps = {
  mode: null,
  language: null,
  width: null,
  minDatetime: null,
  maxDatetime: null,
  labels: {
    type: 'HTML',
    widthVAxis: 60, // 60*5, if you define it, recommend use <maxVAxis> * 60, because each vAxis has 59px
    showHAxis: false,
  },
};
